// Loan Financials — Fees ledger TanStack Query hooks. Reads register under
// the co-located key factory; both mutations invalidate the loan's fee list,
// the cross-loan prefix, and loan HEALTH (a fee is a loan-state fact the
// Overview attention feed / tab badges may surface). Toasts + ApiError
// rendering happen in the calling component.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  loanFeesApi,
  type CreateLoanFeeRequest,
  type CrossLoanFeesParams,
  type FeeReviewQueueParams,
  type ObligationCreationExplainRequest,
  type UpdateLoanFeeRequest,
} from "@/api/portfolioAdmin/loanFeesApi";
import { invalidateLoanHealth } from "@/hooks/portfolioAdmin/invalidation";
import { loanFeesKeys } from "@/hooks/portfolioAdmin/loanFeesKeys";
import { forwardProjectionKeys } from "@/hooks/portfolioAdmin/forwardProjectionKeys";
import { obligationLedgerKeys } from "@/hooks/portfolioAdmin/obligationLedgerKeys";
import { servicingKeys } from "@/hooks/portfolioAdmin/useServicing";
import { invalidateCycleScope } from "@/hooks/portfolioAdmin/useMonthlyCycle";

/** The PR-5 standing worksheet + Income Pools forward overlay — a DIFFERENT
 *  query family from the servicing ledger below. "the ledger" here means the
 *  worksheet's own reads (approved fees / open obligations); it does NOT cover
 *  `servicingKeys.ledger`, which is why that needs its own call. */
function invalidateForwardProjection(
  qc: ReturnType<typeof useQueryClient>,
  loanId: string,
) {
  void qc.invalidateQueries({ queryKey: forwardProjectionKeys.projectionPrefix(loanId) });
  void qc.invalidateQueries({ queryKey: forwardProjectionKeys.incomePoolsForwardPrefix(loanId) });
}

export function useLoanFees(loanId: string | undefined) {
  return useQuery({
    queryKey: loanFeesKeys.loan(loanId ?? ""),
    queryFn: () => loanFeesApi.forLoan(loanId!),
    enabled: !!loanId,
  });
}

export function useCrossLoanFees(params?: CrossLoanFeesParams) {
  return useQuery({
    queryKey: loanFeesKeys.crossLoan(params),
    queryFn: () => loanFeesApi.crossLoan(params),
  });
}

export function useCreateLoanFee(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLoanFeeRequest) => loanFeesApi.create(loanId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: loanFeesKeys.loan(loanId) });
      void qc.invalidateQueries({ queryKey: loanFeesKeys.crossLoanPrefix() });
      // A borrower-payable manual fee lands pending_review — the B3 review
      // section on the same tab (and the cross-loan queue) must refresh.
      void qc.invalidateQueries({ queryKey: loanFeesKeys.reviewQueuePrefix() });
      void invalidateLoanHealth(qc, loanId);
      invalidateForwardProjection(qc, loanId);
    },
  });
}

export function useUpdateLoanFee(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { feeId: string; body: UpdateLoanFeeRequest }) =>
      loanFeesApi.update(loanId, vars.feeId, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: loanFeesKeys.loan(loanId) });
      void qc.invalidateQueries({ queryKey: loanFeesKeys.crossLoanPrefix() });
      // Edits to a pending_review fee (e.g. amount) render in the queue too.
      void qc.invalidateQueries({ queryKey: loanFeesKeys.reviewQueuePrefix() });
      void invalidateLoanHealth(qc, loanId);
      invalidateForwardProjection(qc, loanId);
    },
  });
}

// --- Add Obligation wizard (PR-2) ---------------------------------------------

/** The wizard's catalog read — curated addable obligation types with their
 *  live WF1 slot facts + the full ladder for the consequence visual. */
export function useObligationCatalog(loanId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: loanFeesKeys.obligationCatalog(loanId ?? ""),
    queryFn: () => loanFeesApi.obligationCatalog(loanId!),
    enabled: enabled && !!loanId,
  });
}

export function useExplainObligationCreation() {
  return useMutation({
    // no-invalidation: read-only AI explanation; no cached reads change
    mutationFn: (args: { loanId: string; body: ObligationCreationExplainRequest }) =>
      loanFeesApi.explainObligationCreation(args.loanId, args.body),
  });
}

// --- Fee Review (system-assessed fees, B3 approve/waive) ---------------------

export function useFeeReviewQueue(params?: FeeReviewQueueParams, enabled = true) {
  return useQuery({
    queryKey: loanFeesKeys.reviewQueue(params),
    queryFn: () => loanFeesApi.reviewQueue(params),
    // The loan-scoped review section self-gates on Fees.Review — pass
    // enabled=false so users without the permission never fire the request.
    enabled,
  });
}

/** Approve/waive act on a cross-loan queue row, so the row's loanId rides
 *  the mutation vars — its per-loan fee list + health invalidate alongside
 *  the review-queue and cross-loan prefixes. */
function invalidateFeeReview(qc: ReturnType<typeof useQueryClient>, loanId: string) {
  void qc.invalidateQueries({ queryKey: loanFeesKeys.reviewQueuePrefix() });
  void qc.invalidateQueries({ queryKey: loanFeesKeys.loan(loanId) });
  void qc.invalidateQueries({ queryKey: loanFeesKeys.crossLoanPrefix() });
  void invalidateLoanHealth(qc, loanId);
  // Approve creates the WF1 obligation + bills the fee — the standing
  // worksheet projection changes.
  invalidateForwardProjection(qc, loanId);
  // ApproveAsync runs CreateOrIncrementObligationAsync, which moves the
  // ledger's `openBorrowerFeeObligations` — the Record Payment wizard's
  // prefill anchor (PR #1099), the Payment Tracking summary strip, and the
  // Overview all read it off THIS key. Nothing else here is a prefix of it:
  // loan health is a sibling, and invalidateLoanSummaryAndHealth's per-loan
  // key is `exact: true` on purpose. Without this line the wizard prefills
  // the bare schedule remainder for up to the 5-minute staleTime, so the
  // staff member either under-collects by exactly the fee (the waterfall
  // absorbs it out of Seller Principal) or is warned about "overpayment"
  // for typing the correct total.
  void qc.invalidateQueries({ queryKey: servicingKeys.ledger(loanId) });
  // Approve MINTS the WF1 obligation, and LoanObligationsPanel sits one card
  // below the approve button on this very tab (FeeObligationTab) — an ACTIVE
  // query, so it does not even self-heal on remount. useWaiveObligation
  // already invalidates the fee keys; this is the same coupling in the
  // direction that was never wired.
  void qc.invalidateQueries({ queryKey: obligationLedgerKeys.loan(loanId) });
  void qc.invalidateQueries({ queryKey: obligationLedgerKeys.crossLoanPrefix() });
  // An approved fee bills on the next statement, so it flips that statement's
  // HasUnappliedChanges — the B3 review modal locks approve/send on that flag.
  // Stale, the lock stays disengaged and the reviewer gets a fail-closed 400
  // instead of a disabled button.
  invalidateCycleScope(qc, loanId);
}

export function useApproveFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { feeId: string; loanId: string }) =>
      loanFeesApi.approve(vars.feeId),
    onSuccess: (_data, vars) => invalidateFeeReview(qc, vars.loanId),
  });
}

export function useWaiveFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { feeId: string; loanId: string; reason: string }) =>
      loanFeesApi.waive(vars.feeId, vars.reason),
    onSuccess: (_data, vars) => invalidateFeeReview(qc, vars.loanId),
  });
}
